import AppForms from "@toothfairy/shared-ui/AppForms";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppExpander from "@toothfairy/shared-ui/AppExpander";
import AppTable from "@toothfairy/shared-ui/AppTable";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { View, ActivityIndicator, useWindowDimensions } from "react-native";
import dayjs from "dayjs";
import { AppIcon, AppText } from "@toothfairy/shared-ui";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyChoice from "../../../../Components/ToothFairyComponents/ToothFairyChoice";
import Constants from "../../../../Constants";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import useSettingsChipHref from "../../../../Hooks/useSettingsChipHref";
import useKnowledgeHubChipHref from "../../../../Hooks/useKnowledgeHubChipHref";
import entitySnapshotsHelper from "../../../../API/EntitySnapshots/entitySnapshotsHelper";
import Color from "../../../../Utility/Color";
import IdCopyView from "../../../../Components/ToothFairyComponents/IdCopyView";
import { isLargeScreen } from "../../../MainViewContainer";
import objectDiffUtils from "../../../../Utils/objectDiffUtils";
import semverHelper from "../../../../Utils/semverHelper";
import { Popable } from "react-native-popable";
import VersionDiffSummary from "../../../../Components/VersionComparison/VersionDiffSummary";
import VersionDiffView from "../../../../Components/VersionComparison/VersionDiffView";
import RelationshipPanel from "./RelationshipPanel";
import ToolTestModal from "./ToolTestModal";
import {
  BuilderBareColumn,
  BuilderSection,
  PassthroughSection,
  useBuilderDirtyTracking,
} from "./BuilderSections";

const HOOK_SECTION_FIELD_MAP = {
  profile: ["name", "description"],
  environment: ["customExecutionEnvironment"],
  code: ["customExecutionInstructions", "customExecutionCode"],
  resources: ["staticDocs", "staticArtifacts", "customExecutionSecrets"],
  behaviour: ["hardcodedScript", "isTemplate"],
  versions: ["customCodeExecutionEnvironmentVersion"],
};

const CustomExecutionSettingsAIView = ({
  isEdit,
  settingType,
  handleDeletion,
  selectedId,
  availableAuthorisations,
  isAuthorisationsLoaded,
  isBuilderLayout,
  builderActiveSection,
  onBuilderContextChange,
  saveVersion,
}) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const SECRET_TYPE_LABELS = {
    apikey: t("settings.authorisation.types.apiKey"),
    bearer: t("settings.authorisation.types.bearer"),
    oauth: t("settings.authorisation.types.oauth2"),
    password: t("settings.authorisation.types.password"),
    env: t("settings.authorisation.types.environmentVariable"),
    username_and_password: t(
      "settings.authorisation.types.usernameAndPassword"
    ),
    generic: t("settings.authorisation.types.genericSecret"),
    basic: t("settings.authorisation.types.basicAuth"),
  };
  const getSecretTypeLabel = (type) => SECRET_TYPE_LABELS[type] || type || "";
  const [filteredDocs, setFilteredDocs] = React.useState([]);
  const [filteredArtifacts, setFilteredArtifacts] = React.useState([]);
  const entityName = "Code exec.";
  const AppToasty = AppToast.useToast();
  const [lastOpened, setLastOpened] = useState(Date.now());
  const [isDataReady, setIsDataReady] = useState(false);
  const [isNormalizationComplete, setIsNormalizationComplete] = useState(false);
  const initialValuesRef = React.useRef(null);
  const currentIdRef = React.useRef(null);
  const [hookSnapshots, setHookSnapshots] = useState([]);
  const [isLoadingSnapshots, setIsLoadingSnapshots] = useState(false);
  const [hasLoadedSnapshots, setHasLoadedSnapshots] = useState(false);
  const [showRollbackChoice, setShowRollbackChoice] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [rollbackData, setRollbackData] = useState(null);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [snapshotChangeCounts, setSnapshotChangeCounts] = useState({});
  const [showDiffSummary, setShowDiffSummary] = useState(false);
  const [showDiffView, setShowDiffView] = useState(false);
  const [currentDiffChanges, setCurrentDiffChanges] = useState(null);
  const { state } = AppStateManager.useAppStateManager();
  const { getActiveWorkspace, getUserName } = useWorkspaces();
  const chipHrefAuthorisations = useSettingsChipHref("AUTHORISATIONS");
  const chipHrefKnowledgeHubDoc = useKnowledgeHubChipHref();
  const { height } = useWindowDimensions();
  const _title = useCallback(() => {
    return isEdit ? t("settings.list.save") : t("settings.list.create");
  }, [entityName, isEdit, t]);

  const handleVersionPress = (item, index) => {
    const snapshot = hookSnapshots[index];
    if (snapshot?.snapshotData) {
      try {
        const parsedData = JSON.parse(snapshot.snapshotData);

        const differences = objectDiffUtils.calculateDifferences(
          parsedData,
          values,
          {
            excludeFields: [
              "id",
              "createdAt",
              "updatedAt",
              "_version",
              "_deleted",
              "_lastChangedAt",
              "workspaceID",
            ],
          }
        );

        setRollbackData(parsedData);
        setSelectedSnapshot(snapshot);
        setCurrentDiffChanges(differences);
        setShowDiffSummary(true);
      } catch (error) {
        console.error("Failed to parse snapshot data:", error);
        AppToasty.show(t("hooks.failedLoadHistory"), { type: "danger" });
      }
    }
  };

  const handleRollbackConfirm = () => {
    if (rollbackData) {
      const excludeFields = [
        "id",
        "createdAt",
        "updatedAt",
        "_version",
        "_deleted",
        "_lastChangedAt",
        "workspaceID",
      ];
      Object.keys(rollbackData).forEach((key) => {
        if (!excludeFields.includes(key)) {
          setFieldValue(key, rollbackData[key]);
        }
      });
      setHistoryExpanded(false);
      AppToasty.show(t("hooks.historicalVersionLoaded"), {
        type: "info",
      });
    }
    setShowDiffSummary(false);
    setShowDiffView(false);
    setShowRollbackChoice(false);
    setRollbackData(null);
    setSelectedSnapshot(null);
    setCurrentDiffChanges(null);
  };

  const handleHistoryExpandChange = (isExpanded) => {
    setHistoryExpanded(isExpanded);
    handleSnapshotsExpandChange(isExpanded);
  };

  const handleSnapshotsExpandChange = async (isExpanded) => {
    if (isExpanded && !hasLoadedSnapshots && values?.id) {
      setIsLoadingSnapshots(true);
      try {
        const response = await entitySnapshotsHelper.getSnapshotsByEntity(
          values.id,
          {
            sortDirection: "DESC",
            limit: 50,
          }
        );
        const snapshots = response?.data?.snapshotsByEntity?.items || [];
        setHookSnapshots(snapshots);
        setHasLoadedSnapshots(true);

        const counts = {};
        snapshots.forEach((snapshot, index) => {
          if (snapshot?.snapshotData) {
            try {
              const parsedData = JSON.parse(snapshot.snapshotData);
              const differences = objectDiffUtils.calculateDifferences(
                parsedData,
                values,
                {
                  excludeFields: [
                    "id",
                    "createdAt",
                    "updatedAt",
                    "_version",
                    "_deleted",
                    "_lastChangedAt",
                    "workspaceID",
                  ],
                }
              );
              counts[index] = differences.totalChanges;
            } catch (e) {
              counts[index] = 0;
            }
          }
        });
        setSnapshotChangeCounts(counts);
      } catch (error) {
        console.error("Error loading hook snapshots:", error);
      }
      setIsLoadingSnapshots(false);
    }
  };

  const handleViewFullDiff = () => {
    setShowDiffSummary(false);
    setShowDiffView(true);
  };

  const handleDiffSummaryRollback = () => {
    setShowDiffSummary(false);
    handleRollbackConfirm();
  };

  const handleDiffSummaryClose = () => {
    setShowDiffSummary(false);
    setRollbackData(null);
    setSelectedSnapshot(null);
    setCurrentDiffChanges(null);
  };

  const { setFieldValue, values, initialValues, errors, submitCount } =
    AppForms.useFormikContext();

  const _showBuilderSection = (sectionId) =>
    builderActiveSection == null || builderActiveSection === sectionId;
  const SectionRenderer = isBuilderLayout ? BuilderSection : PassthroughSection;
  const BareSectionRenderer = isBuilderLayout
    ? BuilderBareColumn
    : PassthroughSection;

  useBuilderDirtyTracking({
    isBuilderLayout,
    values,
    initialValues,
    saveVersion,
    onBuilderContextChange,
    baselineRefreshTrigger: isNormalizationComplete,
    errors,
    submitCount,
    sectionFieldMap: HOOK_SECTION_FIELD_MAP,
  });

  useEffect(() => {
    const currentId = values?.id || "new";
    if (currentIdRef.current !== currentId) {
      return;
    }
    const savedVersion =
      initialValuesRef.current?.customCodeExecutionEnvironmentVersion;
    if (!isEdit) {
      if (!values?.customCodeExecutionEnvironmentVersion)
        setFieldValue("customCodeExecutionEnvironmentVersion", "0.1.0");
    } else if (!values?.customCodeExecutionEnvironmentVersion) {
      setFieldValue(
        "customCodeExecutionEnvironmentVersion",
        savedVersion ? semverHelper.suggestNext(savedVersion) : "0.1.0"
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, values?.id, isNormalizationComplete]);

  // CRITICAL: Reset state when modal/item changes to prevent stale data
  useEffect(() => {
    const currentId = values?.id || "new";
    if (currentIdRef.current !== currentId) {
      currentIdRef.current = currentId;
      setIsDataReady(false);
      setIsNormalizationComplete(false);
      initialValuesRef.current = null;
      setLastOpened(Date.now());
    }
  }, [values?.id]);

  // CRITICAL: Capture initial values ONCE when they first arrive for this item
  useEffect(() => {
    const currentId = values?.id || "new";

    // Only capture if we haven't already for this item
    if (
      initialValuesRef.current?.capturedForId !== currentId &&
      values !== undefined
    ) {
      initialValuesRef.current = {
        capturedForId: currentId,
        staticDocs: values?.staticDocs,
        staticArtifacts: values?.staticArtifacts,
        customExecutionSecrets: values?.customExecutionSecrets,
        customCodeExecutionEnvironmentVersion:
          values?.customCodeExecutionEnvironmentVersion,
      };
    }
  }, [
    values?.id,
    values?.staticDocs,
    values?.staticArtifacts,
    values?.customExecutionSecrets,
  ]);

  // CRITICAL: Ensure data is fully loaded before rendering to prevent data loss
  useEffect(() => {
    // Wait for all required data to be available including populated arrays
    const hasRequiredData =
      state?.mrcDocuments !== undefined &&
      isAuthorisationsLoaded === true &&
      Array.isArray(availableAuthorisations) &&
      values !== undefined &&
      initialValuesRef.current !== null;

    if (!hasRequiredData || isNormalizationComplete) {
      return;
    }

    // Use the captured initial values to prevent race conditions
    const capturedStaticDocs = initialValuesRef.current.staticDocs;
    const capturedStaticArtifacts = initialValuesRef.current.staticArtifacts;
    const capturedSecrets = initialValuesRef.current.customExecutionSecrets;

    // Parse staticDocs if it's a string
    let parsedStaticDocs;
    if (typeof capturedStaticDocs === "string") {
      try {
        parsedStaticDocs = JSON.parse(capturedStaticDocs);
      } catch (e) {
        parsedStaticDocs = [];
      }
    } else {
      parsedStaticDocs = capturedStaticDocs;
    }

    // Normalize and hydrate staticDocs with full document objects
    let normalizedStaticDocs = [];
    if (
      Array.isArray(parsedStaticDocs) &&
      parsedStaticDocs.length > 0 &&
      state?.mrcDocuments?.length > 0
    ) {
      normalizedStaticDocs = parsedStaticDocs
        .map((doc) => {
          const docId = typeof doc === "string" ? doc : doc?.id;
          const fullDoc = state?.mrcDocuments?.find((d) => d?.id === docId);
          if (fullDoc) {
            return {
              id: fullDoc.id,
              name: fullDoc.title,
              value: fullDoc.id,
              label: fullDoc.title,
              title: fullDoc.title,
              external_path: fullDoc.external_path,
            };
          }
          // Keep original if already hydrated
          if (doc?.id && doc?.label) return doc;
          return null;
        })
        .filter(Boolean);
    } else if (Array.isArray(parsedStaticDocs)) {
      // Already hydrated or empty
      normalizedStaticDocs = parsedStaticDocs.filter(Boolean);
    }

    // Parse customExecutionSecrets if it's a string
    let parsedSecrets;
    if (typeof capturedSecrets === "string") {
      try {
        parsedSecrets = JSON.parse(capturedSecrets);
      } catch (e) {
        parsedSecrets = [];
      }
    } else {
      parsedSecrets = capturedSecrets;
    }

    // Normalize and hydrate customExecutionSecrets with full authorisation objects
    let normalizedSecrets = [];
    if (
      Array.isArray(parsedSecrets) &&
      parsedSecrets.length > 0 &&
      availableAuthorisations?.length > 0
    ) {
      normalizedSecrets = parsedSecrets
        .map((secret) => {
          const secretId = typeof secret === "string" ? secret : secret?.id;
          const fullAuth = availableAuthorisations?.find(
            (a) => a?.id === secretId
          );
          if (fullAuth) {
            return {
              id: fullAuth.id,
              name: fullAuth.name,
              value: fullAuth.id,
              label: fullAuth.name,
              type: fullAuth.type,
            };
          }
          // Keep original if already hydrated
          if (secret?.id && secret?.label) return secret;
          return null;
        })
        .filter(Boolean);
    } else if (Array.isArray(parsedSecrets)) {
      // Already hydrated or empty
      normalizedSecrets = parsedSecrets.filter(Boolean);
    }

    // Set field values only once with the normalized captured values
    setFieldValue("staticDocs", normalizedStaticDocs);

    // Parse staticArtifacts if it's a string
    let parsedStaticArtifacts;
    if (typeof capturedStaticArtifacts === "string") {
      try {
        parsedStaticArtifacts = JSON.parse(capturedStaticArtifacts);
      } catch (e) {
        parsedStaticArtifacts = [];
      }
    } else {
      parsedStaticArtifacts = capturedStaticArtifacts;
    }

    // Normalize and hydrate staticArtifacts with full document objects
    let normalizedStaticArtifacts = [];
    if (
      Array.isArray(parsedStaticArtifacts) &&
      parsedStaticArtifacts.length > 0 &&
      state?.mrcDocuments?.length > 0
    ) {
      normalizedStaticArtifacts = parsedStaticArtifacts
        .map((doc) => {
          const docId = typeof doc === "string" ? doc : doc?.id;
          const fullDoc = state?.mrcDocuments?.find((d) => d?.id === docId);
          if (fullDoc) {
            const ext = fullDoc.external_path?.split(".")?.pop();
            return {
              id: fullDoc.id,
              name: `${fullDoc.title}${ext ? "." + ext : ""}`,
              value: fullDoc.id,
              label: `${fullDoc.title}${ext ? "." + ext : ""}`,
              title: fullDoc.title,
              external_path: fullDoc.external_path,
            };
          }
          // Keep original if already hydrated
          if (doc?.id && doc?.label) return doc;
          return null;
        })
        .filter(Boolean);
    } else if (Array.isArray(parsedStaticArtifacts)) {
      // Already hydrated or empty
      normalizedStaticArtifacts = parsedStaticArtifacts.filter(Boolean);
    }

    setFieldValue("staticArtifacts", normalizedStaticArtifacts);
    setFieldValue("customExecutionSecrets", normalizedSecrets);

    // Mark normalization as complete to prevent re-running
    setIsNormalizationComplete(true);
    setIsDataReady(true);
  }, [
    values?.id,
    state?.mrcDocuments,
    availableAuthorisations,
    isAuthorisationsLoaded,
    values,
    isNormalizationComplete,
    setFieldValue,
  ]);
  useEffect(() => {
    let filteredObjects = state?.mrcDocuments
      ?.filter((obj) => {
        return obj?.external_path?.endsWith(".py");
      })
      ?.map((u) => {
        return {
          ...u,
          backgroundColor: currentTheme?.theme?.primary,
          name: u?.title,
          category: "docs",
          label: u?.title,
          value: u?.id,
        };
      });
    setFilteredDocs(filteredObjects);
  }, [state?.mrcDocuments?.length]);
  useEffect(() => {
    const allDocs = state?.mrcDocuments;
    let filteredArtifactObjects = allDocs
      ?.filter((obj) => {
        return obj?.type === "designArtifact";
      })
      ?.map((u) => {
        const ext = u?.external_path?.split(".")?.pop();
        return {
          ...u,
          backgroundColor: currentTheme?.theme?.primary,
          name: `${u?.title}${ext ? "." + ext : ""}`,
          category: "artifacts",
          label: `${u?.title}${ext ? "." + ext : ""}`,
          value: u?.id,
        };
      });
    setFilteredArtifacts(filteredArtifactObjects);
  }, [state?.mrcDocuments?.length]);
  const colourStyles = {
    control: (styles) => ({
      ...styles,
      backgroundColor: currentTheme?.theme?.inputBackgroundColor,
      borderColor: currentTheme?.theme?.border_color,
      fontFamily: "normalFont",
      fontSize: currentTheme?.theme?.small_font_size,
      borderRadius: 10,
    }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
      let color = new Color(data?.backgroundColor || currentTheme?.theme?.grey);
      return {
        ...styles,
        backgroundColor: isFocused
          ? currentTheme?.theme?.primary
          : currentTheme?.theme?.transparent,
        color: currentTheme?.theme?.fontColor,
        cursor: "pointer",
      };
    },
    multiValue: (styles, { data }) => {
      let color = new Color(data?.backgroundColor || currentTheme?.theme?.grey);
      return {
        ...styles,
        color: currentTheme?.theme?.fontColor,
        backgroundColor: currentTheme?.theme?.primary,
      };
    },
    multiValueLabel: (styles, { data }) => ({
      ...styles,
      color: currentTheme?.theme?.white,
    }),
    multiValueRemove: (styles, { data }) => ({
      ...styles,
      color: currentTheme?.theme?.primary,
      ":hover": {
        backgroundColor: currentTheme?.theme?.primary,
        color: "white",
      },
    }),
    menu: (styles) => ({
      ...styles,
      backgroundColor: currentTheme?.theme?.mediumGrey,
      borderColor: currentTheme?.theme?.lightGrey,
      fontFamily: "normalFont",
      fontSize: currentTheme?.theme?.small_font_size,
    }),
  };

  // Show loading indicator while data is being prepared
  // CRITICAL: Do not render form fields until normalization is complete to prevent data loss
  if (!isDataReady || !isNormalizationComplete) {
    return (
      <View
        style={{
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
          padding: 20,
        }}
      >
        <ActivityIndicator size="large" color={currentTheme?.theme?.primary} />
        <AppText style={{ marginTop: 10 }}>Loading form data...</AppText>
      </View>
    );
  }

  return (
    <>
      {_showBuilderSection("profile") && (
        <SectionRenderer
          title={t("hooks.builderSectionProfile", {
            defaultValue: "Overview",
          })}
          icon={AppAssets.icons.persona}
        >
          <View>
            {isEdit && <IdCopyView id={values?.id} />}
            <AppForms.AppFormField
              showTextInputLabel
              isMandatory
              wrapperInputStyle={{
                paddingTop: 10,
              }}
              label={t("hooks.executionHookName")}
              placeholder={t("hooks.executionHookNamePlaceholder")}
              keyboardtype="default"
              textContextType="label"
              autoCorrect={false}
              name="name"
            />
            <AppForms.AppFormField
              showTextInputLabel
              isMandatory
              wrapperInputStyle={{
                paddingTop: 10,
              }}
              multiline
              textContainer={{
                minHeight: 180,
                paddingVertical: 10,
                marginHorizontal: 5,
              }}
              label={t("settings.list.description")}
              placeholder={t("hooks.executionHookDescPlaceholder")}
              keyboardtype="default"
              textContextType="label"
              autoCorrect={false}
              name="description"
            />
          </View>
        </SectionRenderer>
      )}
      {_showBuilderSection("environment") && (
        <SectionRenderer
          title={t("hooks.builderSectionEnvironment", {
            defaultValue: "Execution environment",
          })}
          icon={AppAssets.icons.terminal}
        >
          <View>
            <AppForms.AppFormDropDown
              isElevated={false}
              dropDownStyle={{
                borderColor: currentTheme?.theme?.border_color,
                maxWidth: !isLargeScreen() && 250,
              }}
              showTextInputLabel
              label={t("hooks.customExecutionEnv")}
              isMandatory
              name="customExecutionEnvironment"
              items={
                state?.exec_envs_container?.length > 0
                  ? [
                      { id: "none", name: t("hooks.useDefaultExecEnv") },
                      ...state?.exec_envs_container,
                    ]
                  : [{ id: "none", name: t("hooks.useDefaultExecEnv") }]
              }
              propertyLabel="name"
              propertyValue="id"
              _onValueChange={(v) => {
                if (v != "none") {
                  const selectedEnv = state?.exec_envs_container?.find(
                    (env) => env?.id == v
                  );
                  setFieldValue("codeExecutionEnvironmentID", v);
                  setFieldValue("functionName", selectedEnv?.name);
                } else {
                  setFieldValue("codeExecutionEnvironmentID", null);
                  setFieldValue("functionName", null);
                }
              }}
              placeholder={t("hooks.selectCustomExecEnv")}
            />
            <AppText
              style={{
                paddingLeft: 5,
                fontSize: currentTheme?.theme?.extra_small_font_size,
              }}
              wrapperStyle={{
                top: 0,
                justifyContent: "center",
                marginLeft: 10,
              }}
              iconSize={12}
              leftIcon={AppAssets?.icons?.question_icon}
            >
              {t("hooks.customExecutionEnvInfo")}
            </AppText>
          </View>
        </SectionRenderer>
      )}
      {_showBuilderSection("code") && (
        <SectionRenderer
          title={t("hooks.builderSectionCode", {
            defaultValue: "Code",
          })}
          icon={AppAssets.icons.code}
        >
          <View>
            <AppForms.AppFormField
              showTextInputLabel
              wrapperInputStyle={{
                paddingTop: 10,
              }}
              multiline
              textContainer={{
                minHeight: 180,
                paddingVertical: 10,
                marginHorizontal: 5,
              }}
              label={t("hooks.codeExecInstructions")}
              placeholder={t("hooks.codeExecInstructionsPlaceholder")}
              keyboardtype="default"
              textContextType="label"
              autoCorrect={false}
              name="customExecutionInstructions"
            />
            <AppForms.AppFormCodeEditor
              showTextInputLabel
              wrapperInputStyle={{
                paddingTop: 10,
              }}
              multiline
              textContainer={{
                minHeight: 360,
                paddingVertical: 10,
              }}
              label={t("hooks.predefinedCodeSnippet")}
              placeholder={t("hooks.predefinedCodeSnippetPlaceholder")}
              keyboardtype="default"
              textContextType="label"
              autoCorrect={false}
              name="customExecutionCode"
            />
          </View>
        </SectionRenderer>
      )}
      {_showBuilderSection("resources") && (
        <SectionRenderer
          title={t("hooks.builderSectionResources", {
            defaultValue: "Static resources",
          })}
          icon={AppAssets.icons.collection}
        >
          <View>
            {/* CRITICAL: Always render staticDocs field to prevent data loss */}
            <>
              <AppForms.AppFormMultiSelect
                showTextInputLabel
                inputLabelStyle={{
                  marginLeft: 10,
                  marginBottom: 10,
                }}
                key={`staticdocs-${lastOpened}-${values?.id || "new"}`}
                label={t("hooks.assignStaticPythonFiles")}
                name="staticDocs"
                chipHref={chipHrefKnowledgeHubDoc}
                topErrorOffset={-21}
                touchedByDefault
                placeholder={t("hooks.assignStaticPythonFilesPlaceholder")}
                hidePlaceholder={true}
                items={filteredDocs}
                options={filteredDocs.map((u) => {
                  return {
                    ...u,
                    value: u?.id,
                  };
                })}
                uniqueKey="id"
                showDropDowns={true}
                readOnlyHeadings={true}
                labelOption="title"
                groupByProperty="embedding_status"
                style={{ maxWidth: 800 }}
                wrapperContainerStyle={{
                  zIndex: 20,
                  paddingTop: 10,
                  marginBottom: 0,
                }}
                styles={colourStyles}
                maxSelectedValues={10}
                onError={(error) => {
                  AppToasty.show(t("hooks.maxStaticPythonFilesReached"), {
                    type: "warning",
                  });
                }}
                description={
                  values?.staticDocs?.length >= 10
                    ? t("hooks.maxStaticPythonFilesReached")
                    : t("hooks.selectUpTo10StaticPythonFiles")
                }
                descriptionIcon={false}
                descriptionColor={currentTheme?.theme?.mediumGrey}
              />
            </>

            {/* CRITICAL: Always render staticArtifacts field to prevent data loss */}
            <>
              <AppForms.AppFormMultiSelect
                showTextInputLabel
                inputLabelStyle={{
                  marginLeft: 10,
                  marginBottom: 10,
                }}
                key={`staticartifacts-${lastOpened}-${values?.id || "new"}`}
                label={t("hooks.assignStaticArtifacts")}
                name="staticArtifacts"
                chipHref={chipHrefKnowledgeHubDoc}
                topErrorOffset={-21}
                touchedByDefault
                placeholder={t("hooks.assignStaticArtifactsPlaceholder")}
                hidePlaceholder={true}
                items={filteredArtifacts}
                options={filteredArtifacts.map((u) => {
                  return {
                    ...u,
                    value: u?.id,
                  };
                })}
                uniqueKey="id"
                showDropDowns={true}
                readOnlyHeadings={true}
                labelOption="title"
                groupByProperty="embedding_status"
                style={{ maxWidth: 800 }}
                wrapperContainerStyle={{
                  zIndex: 10,
                  paddingTop: 10,
                  marginBottom: 0,
                }}
                styles={colourStyles}
                maxSelectedValues={50}
                onError={(error) => {
                  AppToasty.show(t("hooks.maxStaticArtifactsReached"), {
                    type: "warning",
                  });
                }}
                description={
                  values?.staticArtifacts?.length >= 50
                    ? t("hooks.maxStaticArtifactsReached")
                    : t("hooks.selectUpTo50StaticArtifacts")
                }
                descriptionIcon={false}
                descriptionColor={currentTheme?.theme?.mediumGrey}
              />
            </>

            {/* CRITICAL: Always render customExecutionSecrets field to prevent data loss */}
            <AppForms.AppFormMultiSelect
              showTextInputLabel
              inputLabelStyle={{
                marginLeft: 10,
                marginBottom: 10,
              }}
              key={`secrets-${lastOpened}-${values?.id || "new"}`}
              label={t("hooks.authAndSecretsToInject")}
              name="customExecutionSecrets"
              chipHref={chipHrefAuthorisations}
              topErrorOffset={-21}
              touchedByDefault
              placeholder={t("hooks.authAndSecretsPlaceholder")}
              hidePlaceholder={true}
              items={availableAuthorisations
                ?.filter((u) => u?.type != "username_and_password")
                .map((u) => {
                  const typeLabel = getSecretTypeLabel(u?.type);
                  return {
                    ...u,
                    value: u?.id,
                    label: typeLabel ? `${u?.name} (${typeLabel})` : u?.name,
                    category: u?.type,
                  };
                })}
              uniqueKey="id"
              showDropDowns={true}
              readOnlyHeadings={true}
              labelOption="name"
              groupByProperty="type"
              style={{ maxWidth: 800 }}
              wrapperContainerStyle={{
                flex: !isLargeScreen() ? 1 : 0.5,
                zIndex: 5,
                paddingTop: 10,
                marginBottom: 40,
              }}
              styles={colourStyles}
              description={t("hooks.authAndSecretsPlaceholder")}
              descriptionIcon={false}
              descriptionColor={currentTheme?.theme?.mediumGrey}
              options={availableAuthorisations
                ?.filter((u) => u?.type != "username_and_password")
                ?.map((u) => {
                  const typeLabel = getSecretTypeLabel(u?.type);
                  return {
                    ...u,
                    value: u?.id,
                    label: typeLabel ? `${u?.name} (${typeLabel})` : u?.name,
                    category: u?.type,
                  };
                })}
            />
          </View>
        </SectionRenderer>
      )}
      {_showBuilderSection("behaviour") && (
        <SectionRenderer
          title={t("hooks.builderSectionBehaviour", {
            defaultValue: "Behaviour",
          })}
          icon={AppAssets.icons.bolt}
        >
          <View>
            <AppForms.AppFormSwitch
              // disabled={isEdit}
              // disabled={true}
              wrapperStyle={[
                {
                  maxHeight: 30,
                  // paddingHorizontal: 10,
                  paddingVertical: 18,
                  margin: 5,
                  width: "auto",
                  marginRight: 10,
                },
              ]}
              labelTextStyle={{
                fontSize: currentTheme?.theme?.small_font_size,
                color: currentTheme?.theme?.darkGrey,
              }}
              showTextInputLabel={true}
              label={t("hooks.executeAsStaticScript")}
              displayIsTicked={true}
              name="hardcodedScript"
              value={values?.hardcodedScript}
              disabled={values?.isTemplate}
              description={t("hooks.executeAsStaticScriptDesc")}
              descriptionIcon={false}
              descriptionColor={currentTheme?.theme?.mediumGrey}
            />
            <AppForms.AppFormSwitch
              // disabled={isEdit}
              // disabled={true}
              wrapperStyle={[
                {
                  maxHeight: 30,
                  // paddingHorizontal: 10,
                  borderRadius: 10,
                  paddingVertical: 18,
                  margin: 5,
                  width: "auto",
                  marginRight: 10,
                },
              ]}
              labelTextStyle={{
                fontSize: currentTheme?.theme?.small_font_size,
                color: currentTheme?.theme?.darkGrey,
              }}
              showTextInputLabel={true}
              label={t("hooks.executeAsPythonTool")}
              displayIsTicked={true}
              name="isTemplate"
              value={values?.isTemplate}
              disabled={values?.hardcodedScript}
              description={t("hooks.executeAsPythonToolDesc")}
              descriptionIcon={false}
              descriptionColor={currentTheme?.theme?.mediumGrey}
            />
          </View>
        </SectionRenderer>
      )}
      {!isBuilderLayout && isEdit && (
        <ToothFairyButton
          title={t("tools.testHook", { defaultValue: "Test" })}
          onPress={() => setShowTestModal(true)}
          wrapperStyle={{
            marginTop: 0,
            minWidth: 150,
            height: Constants.appButtonHeight,
          }}
        />
      )}
      {_showBuilderSection("usage") && isEdit && values?.id && (
        <SectionRenderer
          title={t("hooks.builderSectionUsage", { defaultValue: "Usage" })}
          icon={AppAssets.icons.taskAgent}
        >
          <RelationshipPanel
            isEdit={isEdit}
            dependsOn={[
              ...(Array.isArray(values?.customExecutionSecrets)
                ? values.customExecutionSecrets
                    .map((s) => {
                      const secretId = typeof s === "string" ? s : s?.id;
                      const auth = availableAuthorisations?.find(
                        (a) => a?.id === secretId
                      );
                      if (auth) {
                        return {
                          id: auth.id,
                          name: auth.name,
                          type: "authorisation",
                        };
                      }
                      if (s?.id && s?.name) {
                        return {
                          id: s.id,
                          name: s.name,
                          type: "authorisation",
                        };
                      }
                      return null;
                    })
                    .filter(Boolean)
                : []),
            ]}
            usedBy={[
              ...(state?.agents
                ?.filter((a) =>
                  a?.customCodeExecutionEnvironments?.includes(values?.id)
                )
                ?.map((a) => ({
                  id: a.id,
                  name: a.label || a.name,
                  type: "agent",
                })) || []),
              ...(state?.agentsFunctions
                ?.filter((f) => f?.codeExecutionEnvironmentID === values?.id)
                ?.map((f) => ({
                  id: f.id,
                  name: f.name,
                  type: "tool",
                })) || []),
            ]}
          />
        </SectionRenderer>
      )}

      {_showBuilderSection("versions") && isEdit && (
        <SectionRenderer
          title={t("hooks.builderSectionVersions", {
            defaultValue: "Versions",
          })}
          icon={AppAssets.icons.history}
        >
          <View style={{ zIndex: -1, width: "100%" }}>
            <AppExpander
              style={{ minWidth: 250, zIndex: -1 }}
              title={t("hooks.execHookVersionsHistory")}
              icon={AppAssets.icons.history}
              onExpandedChange={handleHistoryExpandChange}
              isExpandedOnLoad={historyExpanded}
            >
              {isLoadingSnapshots ? (
                <View style={{ padding: 20, alignItems: "center" }}>
                  <AppText>{t("hooks.loadingHistory")}</AppText>
                </View>
              ) : hookSnapshots?.length > 0 &&
                getActiveWorkspace()?.subscriptionType != "base" &&
                getActiveWorkspace()?.subscriptionType != "pro" ? (
                <>
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "space-between",
                      paddingHorizontal: 10,
                      paddingVertical: 8,
                      marginBottom: 8,
                      backgroundColor:
                        currentTheme?.theme?.softBackgroundColor || "#f5f5f5",
                      borderRadius: 8,
                    }}
                  >
                    <AppText
                      style={{
                        fontSize: 13,
                        color: currentTheme?.theme?.fontColor,
                      }}
                    >
                      {t("dashboard.versioning.totalVersions", {
                        count: hookSnapshots.length,
                      })}
                    </AppText>
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <AppText
                        style={{
                          fontSize: 13,
                          fontWeight: "600",
                          color: currentTheme?.theme?.primary,
                        }}
                      >
                        {t("dashboard.versioning.totalChanges", {
                          count: Object.values(snapshotChangeCounts).reduce(
                            (sum, count) => sum + count,
                            0
                          ),
                        })}
                      </AppText>
                    </View>
                  </View>
                  <AppTable
                    showPagination
                    itemsPerPage={50}
                    onRowPress={handleVersionPress}
                    style={{
                      zIndex: -1,
                      height:
                        hookSnapshots?.length >= 50 ? height - 95 : height - 75,
                      paddingRight: 0,
                      overflowY: "auto",
                      overflowX: "hidden",
                    }}
                    headers={[
                      {
                        key: "datetime",
                        title: t("settings.qa.date"),
                        width: 0.25,
                      },
                      {
                        key: "changes",
                        title: t("settings.qa.changes"),
                        width: 0.1,
                      },
                      {
                        key: "user",
                        title: t("settings.qa.changedBy"),
                        width: 0.3,
                      },
                      {
                        key: "semantic",
                        title: t("settings.qa.versionLabel"),
                        width: 0.2,
                      },
                      {
                        key: "version",
                        title: "#",
                        width: 0.1,
                      },
                    ]}
                    items={hookSnapshots.map((snapshot, index) => {
                      let semanticVersion = "-";
                      try {
                        const parsed = snapshot?.snapshotData
                          ? JSON.parse(snapshot.snapshotData)
                          : null;
                        if (parsed?.customCodeExecutionEnvironmentVersion) {
                          semanticVersion = `v${parsed.customCodeExecutionEnvironmentVersion}`;
                        }
                      } catch (e) {
                        semanticVersion = "-";
                      }
                      return {
                        datetime:
                          dayjs(snapshot?.updatedAt)?.format(
                            "DD-MM-YYYY HH:mm"
                          ) || " ",
                        changes: `${snapshotChangeCounts[index] || 0}`,
                        user:
                          getUserName(snapshot?.updatedBy) ||
                          getUserName(snapshot?.createdBy) ||
                          "System",
                        semantic: semanticVersion,
                        version: snapshot?.snapshotVersion?.toString() || "-",
                      };
                    })}
                  />
                </>
              ) : (
                <View
                  style={{
                    paddingLeft: 0,
                    alignItems: "center",
                  }}
                >
                  <AppPlaceholderView
                    wrapperStyle={{
                      width: "80%",
                      height: "100%",
                      marginVertical: "auto",
                      marginHorizontal: "auto",
                      alignItems: "center",
                    }}
                    mainCaption="No changes recorded yet"
                    subCaption="Changes to this execution hook will appear here if you have a Business or an Enterprise subscription"
                    size={30}
                    icon={AppAssets.icons.orbit}
                  />
                </View>
              )}
            </AppExpander>
          </View>
        </SectionRenderer>
      )}

      <VersionDiffSummary
        isVisible={showDiffSummary}
        onClose={handleDiffSummaryClose}
        onViewFullDiff={handleViewFullDiff}
        onRollback={handleDiffSummaryRollback}
        snapshotVersion={selectedSnapshot?.snapshotVersion || 1}
        changes={currentDiffChanges}
        datetime={
          dayjs(selectedSnapshot?.updatedAt)?.format("DD-MM-YYYY HH:mm") || ""
        }
        changedBy={
          getUserName(selectedSnapshot?.updatedBy) ||
          getUserName(selectedSnapshot?.createdBy) ||
          "System"
        }
      />

      <VersionDiffView
        isVisible={showDiffView}
        onClose={() => {
          setShowDiffView(false);
          setRollbackData(null);
          setSelectedSnapshot(null);
          setCurrentDiffChanges(null);
        }}
        changes={currentDiffChanges}
        snapshotVersion={selectedSnapshot?.snapshotVersion || 1}
      />

      {_showBuilderSection("versions") && (
        <BareSectionRenderer>
          <View
            style={{
              zIndex: -1000,
              marginHorizontal: 10,
              marginBottom: 16,
              paddingVertical: 4,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 8,
                marginBottom: 8,
              }}
            >
              <AppText
                preventTranslation
                style={{ fontSize: 14, fontWeight: "600" }}
                color={currentTheme?.theme?.font_color}
              >
                {t("settings.qa.versionField")}
              </AppText>
              {initialValuesRef.current
                ?.customCodeExecutionEnvironmentVersion ? (
                <View
                  style={{
                    paddingHorizontal: 8,
                    paddingVertical: 2,
                    borderRadius: 12,
                    backgroundColor: currentTheme?.theme?.orange,
                  }}
                >
                  <AppText
                    preventTranslation
                    style={{ fontSize: 11, fontWeight: "600" }}
                    color={currentTheme?.theme?.white}
                  >
                    v
                    {
                      initialValuesRef.current
                        ?.customCodeExecutionEnvironmentVersion
                    }{" "}
                    ({t("settings.qa.versionCurrentLabel")})
                  </AppText>
                </View>
              ) : null}
            </View>
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 10,
              }}
            >
              <View style={{ flex: 1, maxWidth: 200, marginLeft: -5 }}>
                <AppForms.AppFormField
                  showTextInputLabel={false}
                  keyboardtype="default"
                  autoCorrect={false}
                  name="customCodeExecutionEnvironmentVersion"
                  placeholder="1.2.3"
                  wrapperInputStyle={{ paddingTop: 0, marginLeft: 0 }}
                />
              </View>
              <Popable
                numberOfLines={5}
                backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
                style={[
                  {
                    zIndex: 20,
                    maxWidth: 600,
                  },
                ]}
                content={t("settings.qa.useSuggestedVersion", {
                  version: semverHelper.suggestNext(
                    initialValuesRef.current
                      ?.customCodeExecutionEnvironmentVersion
                  ),
                })}
                action="hover"
                position="bottom"
              >
                <AppIcon
                  icon={AppAssets?.icons["planner-auto"]}
                  isClickable
                  onPress={() =>
                    setFieldValue(
                      "customCodeExecutionEnvironmentVersion",
                      semverHelper.suggestNext(
                        initialValuesRef.current
                          ?.customCodeExecutionEnvironmentVersion
                      )
                    )
                  }
                  wrapperStyle={{
                    minWidth: 120,
                    height: Constants.appButtonHeight - 10,
                    marginTop: -10,
                  }}
                />
              </Popable>
            </View>
            <AppText
              preventTranslation
              style={{ fontSize: 11, marginTop: -8, fontStyle: "italic" }}
              color={currentTheme?.theme?.mediumGrey}
            >
              {t("settings.qa.versionSuggestedHint")}
            </AppText>
          </View>
        </BareSectionRenderer>
      )}
      {!isBuilderLayout && (
        <View style={{ alignItems: "center", zIndex: -1 }}>
          <AppForms.AppSubmitButton
            title={_title()}
            awesomeIconColor={currentTheme?.theme?.white}
            backgroundColor={currentTheme?.theme?.primary}
            btnContentStyle={{
              paddingLeft: 0,
              paddingRight: 0,
            }}
            style={[
              {
                height: Constants.appButtonHeight,
                width: "auto",
                shadowColor: currentTheme?.theme?.transparent,
                marginHorizontal: 10,
              },
            ]}
            // btnLabelStyle={{ fontSize: 16 }}
            wrapperStyle={[
              {
                width: "fit-content",
                minWidth: 150,
                marginTop: 20,
                height: Constants.appButtonHeight,
              },
            ]}
          />

          {isEdit && (
            <ToothFairyButton
              title={t("common.delete")}
              onPress={handleDeletion}
              isDeletion
              wrapperStyle={{
                marginTop: 30,
                minWidth: 150,
                height: Constants.appButtonHeight,
              }}
            />
          )}
        </View>
      )}

      <ToolTestModal
        isModalOpen={showTestModal}
        setIsModalOpen={setShowTestModal}
        mode="hook"
        id={values?.id}
        token={getActiveWorkspace()?.workspaceToken}
        entityName={values?.name}
      />
    </>
  );
};

export default CustomExecutionSettingsAIView;
